<script setup lang="ts">
/**
 * Profile image with an initials fallback.
 *
 * The fallback colour is derived from the user id, so the same person always
 * gets the same colour and a list of avatars stays visually distinguishable
 * even when nobody has uploaded a picture.
 */
import { computed } from 'vue';
import type { MediaAsset } from '@talkvibe/shared';
import { initials } from '../utils/format.ts';

const props = withDefaults(
  defineProps<{
    name: string;
    src?: string | null;
    /**
     * Resized variants, when the server has generated them. Given this, the
     * browser picks a file matched to the rendered size and pixel density
     * instead of downloading a full-size photo for a 36px circle.
     */
    asset?: MediaAsset | null;
    userId?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    online?: boolean | null;
    /**
     * Fills the parent instead of rendering at a fixed circle size - for a
     * tile the caller already sized and clipped, like a story tray tile.
     *
     * **`size` still matters when filling.** It is no longer the rendered box,
     * but it is what tells the browser which `srcset` variant to download, so a
     * large tile left on the default `md` asks for a 44px file and gets a 44px
     * file stretched over it. Pass the size closest to the tile's real width.
     */
    fill?: boolean;
  }>(),
  { src: null, asset: null, userId: '', size: 'md', online: null, fill: false },
);

const SIZES = {
  xs: 'h-7 w-7 text-[0.625rem]',
  sm: 'h-9 w-9 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl sm:h-28 sm:w-28 sm:text-3xl',
} as const;

const DOT_SIZES = {
  xs: 'h-2 w-2',
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
  lg: 'h-3.5 w-3.5',
  xl: 'h-5 w-5',
} as const;

/** Rendered width per size, used to tell the browser which variant to pick. */
const RENDERED_WIDTH: Record<NonNullable<typeof props.size>, number> = {
  xs: 28,
  sm: 36,
  md: 44,
  lg: 64,
  xl: 112,
};

/**
 * `srcset` of the generated variants. Omitted entirely when processing has not
 * finished, so the browser falls back to `src` rather than to a broken URL.
 */
const srcset = computed(() => {
  const variants = props.asset?.variants.filter((v) => v.name !== 'original' && v.width > 0);
  if (!variants || variants.length === 0) return undefined;
  return variants.map((v) => `${v.url} ${v.width}w`).join(', ');
});

/** The size the image actually occupies, so the browser can choose correctly. */
const sizes = computed(() =>
  srcset.value ? `${RENDERED_WIDTH[props.size]}px` : undefined,
);

/** Prefer the processed image; fall back to whatever URL we were given. */
const source = computed(() => props.asset?.url ?? props.src);

const shapeClass = computed(() => (props.fill ? 'h-full w-full' : SIZES[props.size]));
/**
 * The wrapper has to be sized too, or `h-full` on the image resolves against
 * nothing.
 *
 * `inline-flex` sizes itself from its content, so a child asking for 100% of it
 * collapses to the image's intrinsic size. Every caller that used `fill` had
 * quietly worked around this by repeating `class="h-full w-full"` on the
 * component — which is the sign that the prop was not doing what it says.
 */
const rootClass = computed(() => (props.fill ? 'h-full w-full' : ''));
/** The circle gets a hairline ring against any background; a fill tile is
 * clipped by its own container and would just double that edge. */
const roundedClass = computed(() => (props.fill ? '' : 'rounded-full ring-1 ring-border-subtle'));

const PALETTE = [
  'bg-brand-100 text-brand-800',
  'bg-aurora-400/25 text-aurora-600',
  'bg-amber-100 text-amber-800',
  'bg-emerald-100 text-emerald-800',
  'bg-rose-100 text-rose-800',
  'bg-violet-100 text-violet-800',
];

const fallbackColour = computed(() => {
  const seed = props.userId || props.name;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length]!;
});
</script>

<template>
  <span class="relative inline-flex shrink-0" :class="rootClass">
    <img
      v-if="source"
      :src="source"
      :srcset="srcset"
      :sizes="sizes"
      :alt="`${name}'s profile picture`"
      :class="[shapeClass, roundedClass]"
      class="object-cover"
      loading="lazy"
      decoding="async"
    />
    <span
      v-else
      :class="[shapeClass, roundedClass, fallbackColour]"
      class="inline-flex items-center justify-center font-semibold"
      aria-hidden="true"
    >
      {{ initials(name) }}
    </span>
    <!-- Screen readers get the name from the image alt or this label. -->
    <span v-if="!source" class="sr-only">{{ name }}</span>

    <span
      v-if="online !== null"
      :class="[DOT_SIZES[size], online ? 'bg-success' : 'bg-ink-faint']"
      class="absolute right-0 bottom-0 rounded-full ring-2 ring-surface-raised"
      :title="online ? 'Online' : 'Offline'"
    />
  </span>
</template>
